import React, { useState, useEffect, lazy, Suspense } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-scroll';
import { ArrowUpRight } from 'lucide-react';
import { useLang } from './i18n/LanguageContext';

import Hero from './components/Hero';
const About = lazy(() => import('./components/About'));
const Skills = lazy(() => import('./components/Skills'));
const Projects = lazy(() => import('./components/Projects'));
const Experience = lazy(() => import('./components/Experience'));
const Contact = lazy(() => import('./components/Contact'));
const Footer = lazy(() => import('./components/Footer'));

function App() {
  const { lang, toggle, t } = useLang();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const h = document.documentElement;
      const scrolled = h.scrollTop / (h.scrollHeight - h.clientHeight || 1);
      setProgress(Math.min(1, Math.max(0, scrolled)));
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = t.nav.items;

  const LangToggle = ({ className = '' }) => (
    <button
      onClick={toggle}
      className={`label flex items-center gap-1 ${className}`}
      aria-label="Dil / Language"
    >
      <span className={lang === 'en' ? 'text-accent' : 'text-ink/40 hover:text-ink transition-colors'}>EN</span>
      <span className="text-ink/25">/</span>
      <span className={lang === 'tr' ? 'text-accent' : 'text-ink/40 hover:text-ink transition-colors'}>TR</span>
    </button>
  );

  return (
    <div className="min-h-screen bg-paper text-ink">
      {/* Top fixed editorial bar */}
      <header className="fixed top-0 inset-x-0 z-50 bg-paper/90 backdrop-blur-sm border-b border-ink/20">
        <div className="container-ed flex items-center justify-between h-16">
          <Link to="home" smooth duration={500} className="cursor-pointer flex items-center gap-2">
            <span className="text-lg font-extrabold tracking-tightest">Yiğitcan Avaz</span>
            <span className="text-accent text-lg leading-none">®</span>
          </Link>

          <nav className="hidden md:flex items-center gap-7">
            {navItems.map((item) => (
              <Link
                key={item.name}
                to={item.to}
                spy
                smooth
                offset={-64}
                duration={500}
                activeClass="text-accent"
                className="group cursor-pointer label text-ink hover:text-accent transition-colors"
              >
                <span className="text-ink/40 mr-1.5">{item.n}</span>
                {item.name}
              </Link>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-6">
            <LangToggle />
            <Link
              to="contact"
              smooth
              offset={-64}
              duration={500}
              className="inline-flex items-center gap-1.5 label cursor-pointer hover:text-accent transition-colors"
            >
              {t.nav.available}
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-accent opacity-60 animate-ping" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-accent" />
              </span>
            </Link>
          </div>

          <div className="md:hidden flex items-center gap-5">
            <LangToggle />
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="label flex items-center gap-2"
              aria-label="Menu"
            >
              {isMenuOpen ? t.nav.close : t.nav.menu}
            <span className="flex flex-col gap-1">
              <span className={`block h-px w-5 bg-ink transition-transform ${isMenuOpen ? 'translate-y-[3px] rotate-45' : ''}`} />
              <span className={`block h-px w-5 bg-ink transition-transform ${isMenuOpen ? '-translate-y-[2px] -rotate-45' : ''}`} />
            </span>
          </button>
          </div>
        </div>

        {/* scroll progress line */}
        <div className="h-px w-full bg-ink/10">
          <div className="h-px bg-accent origin-left" style={{ transform: `scaleX(${progress})` }} />
        </div>

        {/* Mobile menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden bg-paper border-b border-ink/20 overflow-hidden"
            >
              <div className="container-ed py-4">
                {navItems.map((item) => (
                  <Link
                    key={item.name}
                    to={item.to}
                    spy
                    smooth
                    offset={-64}
                    duration={500}
                    onClick={() => setIsMenuOpen(false)}
                    className="flex items-baseline gap-3 py-3 border-t border-ink/10 cursor-pointer text-2xl font-extrabold tracking-tightest hover:text-accent transition-colors"
                  >
                    <span className="label text-ink/40">{item.n}</span>
                    {item.name}
                  </Link>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      <main key={lang} className="pt-16">
        <Hero />
        <Suspense fallback={null}>
          <About />
          <Skills />
          <Projects />
          <Experience />
          <Contact />
        </Suspense>
      </main>

      <Suspense fallback={null}>
        <Footer key={lang} />
      </Suspense>

      {/* Floating back-to-top — editorial pill */}
      <AnimatePresence>
        {progress > 0.08 && (
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 bg-ink text-paper label hover:bg-accent transition-colors"
            aria-label="Yukarı"
          >
            {t.nav.backToTop}
            <ArrowUpRight size={14} />
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
}

export default App;
